export interface WorkflowResult {
  results: Array<{
    upc: string;
    productTitle: string;
    itemUrl: string;
    sellerUsername: string;
    qualificationStatus: 'Qualified' | 'Unqualified' | 'No Results' | 'Error';
    outreachMessage: string;
    categoryPath?: string;
    sellerDetails?: {
      feedbackScore: number;
      positiveFeedbackPercent: number;
      accountAgeYears: number;
      estimatedItemsSold: number;
      similarProductsCount?: number;
      meetsThresholds: {
        feedback: boolean;
        itemsSold: boolean;
        accountAge: boolean;
        similarProducts?: boolean;
      };
      qualificationReason?: string;
    };
    errorDetails?: string;
  }>;
  summary: {
    totalUPCs: number;
    qualifiedSellers: number;
    messagesGenerated: number;
    unqualifiedSellers: number;
    noResults: number;
    errors: number;
  };
  invalidUPCs?: Array<{
    row: number;
    value: string;
    error: string;
  }>;
}

export interface User {
  id: string;
  email: string;
  role: 'admin' | 'user';
  createdAt?: string;
  updatedAt?: string;
  ebayAccessToken?: string;
  ebayRefreshToken?: string;
  ebayAccessTokenExpiresAt?: string;
  ebayRefreshTokenExpiresAt?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  role?: 'admin' | 'user';
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Token management
const TOKEN_KEY = 'auth_token';

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Helper function to include auth headers
function getHeaders(includeContentType = true): HeadersInit {
  const headers: HeadersInit = {};
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

// Helper function to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // Unauthorized - clear token and redirect to login
    removeAuthToken();
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    throw new Error('Authentication required. Please log in.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// Authentication API
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(credentials),
  });

  const data = await handleResponse<AuthResponse>(response);
  setAuthToken(data.token);
  return data;
}

// Public registration is disabled - only admins can create users via the admin panel
// export async function register(data: RegisterData): Promise<AuthResponse> {
//   const response = await fetch(`${API_BASE_URL}/auth/register`, {
//     method: 'POST',
//     headers: getHeaders(),
//     body: JSON.stringify(data),
//   });
//
//   const result = await handleResponse<AuthResponse>(response);
//   setAuthToken(result.token);
//   return result;
// }

export async function logout(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    headers: getHeaders(),
  });

  await handleResponse(response);
  removeAuthToken();
}

export async function getCurrentUser(): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    method: 'GET',
    headers: getHeaders(),
  });

  return handleResponse<User>(response);
}

export async function verifyToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (response.ok) {
      return true;
    }
    removeAuthToken();
    return false;
  } catch {
    removeAuthToken();
    return false;
  }
}

// User Management API (Admin only)
export async function getAllUsers(): Promise<{ users: User[] }> {
  const response = await fetch(`${API_BASE_URL}/auth/users`, {
    method: 'GET',
    headers: getHeaders(),
  });

  return handleResponse(response);
}

export async function createUser(data: RegisterData): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/auth/users`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
}

export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });

  await handleResponse(response);
}

export async function updateUserRole(userId: string, role: 'admin' | 'user'): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/auth/users/${userId}/role`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ role }),
  });

  return handleResponse(response);
}

export async function changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/password`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
  });

  await handleResponse(response);
}

// Workflow streaming types
export type WorkflowProgressEvent =
  | {
      type: 'start';
      totalUPCs: number;
      totalBatches: number;
    }
  | {
      type: 'batch_start';
      batch: number;
      totalBatches: number;
      upcs: string[];
    }
  | {
      type: 'batch_complete';
      batch: number;
      totalBatches: number;
      results: WorkflowResult['results'];
      summary: {
        qualifiedSellers: number;
        messagesGenerated: number;
        unqualifiedSellers: number;
        noResults: number;
        errors: number;
      };
    }
  | {
      type: 'complete';
      finalResult: WorkflowResult;
    }
  | {
      type: 'error';
      error: string;
      batch?: number;
    };

// Workflow API with streaming support
export async function processWorkflow(
  file: File,
  onProgress: (event: WorkflowProgressEvent) => void
): Promise<WorkflowResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upcColumn', 'UPC');

  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/workflow/process`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 401) {
    removeAuthToken();
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    throw new Error('Authentication required. Please log in.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  // Read SSE stream
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult: WorkflowResult | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      // Decode chunk and add to buffer (if there's data)
      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      // Process complete SSE messages (separated by \n\n)
      const messages = buffer.split('\n\n');
      
      // Keep the last incomplete message in buffer (unless stream is done)
      if (!done) {
        buffer = messages.pop() || '';
      } else {
        // Stream is done - process everything including the last message
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.trim()) {
          // Keep it in messages array for processing
        } else {
          messages.pop(); // Remove empty last element
        }
      }

      for (const message of messages) {
        if (!message.trim() || message.startsWith(':')) {
          // Skip empty lines and comments (keepalive)
          continue;
        }

        // Parse SSE data field
        const dataMatch = message.match(/^data: (.+)$/m);
        if (dataMatch) {
          try {
            const event: WorkflowProgressEvent = JSON.parse(dataMatch[1]);
            
            // Call progress callback
            onProgress(event);

            // Store final result
            if (event.type === 'complete') {
              finalResult = event.finalResult;
            }

            // Handle error events
            if (event.type === 'error') {
              throw new Error(event.error);
            }
          } catch (parseError) {
            console.error('Failed to parse SSE event:', parseError, dataMatch[1]);
          }
        }
      }
      
      // Break after processing final messages
      if (done) {
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!finalResult) {
    throw new Error('Workflow did not complete successfully');
  }

  return finalResult;
}

// eBay OAuth API
export async function getEbayOAuthUrl(): Promise<{ authUrl: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/ebay/oauth`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include', // Include cookies for session-based auth
  });

  return handleResponse<{ authUrl: string }>(response);
}

export async function disconnectEbay(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/ebay/disconnect`, {
    method: 'DELETE',
    headers: getHeaders(),
  });

  return handleResponse<{ message: string }>(response);
}

// Note: MCP tools (search_by_upc, analyze_seller, find_similar_products) are NOT exposed as public API.
// These are internal tools used only by the agent workflow via the /api/workflow/process endpoint.


